<script setup lang="ts">
import SeLink from '@/src/components/Link.vue';
import { useAdvisors, type AdvisorsFilters } from '@/src/composables/useAdvisors';
import SeAdvisorCard from '@/src/modules/advisor/components/advisor-card.vue';
import SeButton from '@/src/ui-kit/button/Button.vue';
import SeIcon from '@/src/ui-kit/icon/Icon.vue';
import { theme } from '@/theme';
import { onMounted, onUnmounted, ref } from 'vue';

const { newAdvisors, advisor } = theme.home;

const hasTriedSecondFetch = ref(false);
const { advisors, fetchAdvisors, perPage } = useAdvisors();

const defaultFilters: AdvisorsFilters = {
  status: '',
  isAuthenticated: false,
  limit: perPage.value,
  sort: 'registered',
  order: 'desc',
};

const loadAdvisors = async (status: string) => {
  try {
    await fetchAdvisors({ ...defaultFilters, status });

    if (advisors.value.length === 0 && status === 'online' && !hasTriedSecondFetch.value) {
      await fetchAdvisors(defaultFilters);
    }
    hasTriedSecondFetch.value = true;
  } catch (error) {
    console.error('Error loading advisors:', error);
    hasTriedSecondFetch.value = true; // Still set to true to hide loader on error
  }
};

onMounted(async () => {
  await loadAdvisors('online');
});

onUnmounted(() => {
  hasTriedSecondFetch.value = false;
});
</script>

<script lang="ts">
export default {
  name: 'SeStaticHomeNewAdvisors',
};
</script>

<template>
  <section v-if="newAdvisors" class="pt-32 pb-56 md:py-56 lg:py-88">
    <div class="advisors-list" :class="advisor?.cardName">
      <div class="w-full md:w-10/12 xl:w-6/12 pb-16 md:pb-32">
        <h2 class="text-h-xl md:text-h-2xl font-semibold pb-8 md:pb-16">
          {{ newAdvisors.title }}
        </h2>
        <p class="text-b-s md:text-b-xl font-medium text-neutral-80">
          {{ newAdvisors.description }}
        </p>
      </div>

      <div class="advisors-list-container">
        <article v-for="advisor of advisors" :key="advisor.id" class="w-full">
          <se-advisor-card :advisor="advisor" />
        </article>
      </div>

      <div class="flex justify-center pt-16 md:pt-32 w-full">
        <se-link href="/advisors/new" class="text-black no-underline w-full md:w-auto">
          <se-button size="large" type="tertiary" class="hidden md:inline-flex md:visible">
            {{ newAdvisors.ctaText }}<se-icon name="arrow-right" :size="20" class="ml-6" />
          </se-button>
          <se-button size="medium" type="tertiary" class="inline-flex md:hidden ml-0 w-full">
            {{ newAdvisors.ctaText }}<se-icon name="arrow-right" :size="16" class="ml-6" />
          </se-button>
        </se-link>
      </div>
    </div>
  </section>
</template>
